import React, { useState } from 'react';
import {
  makeStyles,
  shorthands,
  Button,
  Text,
  tokens,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Avatar,
  Divider,
} from '@fluentui/react-components';
import { PersonRegular, SignOut20Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  header: {
    color: 'white',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    ...shorthands.padding('0', '16px'),
    transitionProperty: 'background-color',
    transitionDuration: '0.3s',
    transitionTimingFunction: 'ease-in-out',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  titleStack: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  titleText: {
    color: 'white',
    fontSize: '15px',
    fontWeight: '600',
    lineHeight: '17px',
  },
  subtitleText: {
    color: 'rgba(255, 255, 255, 0.85)',
    fontSize: '11px',
    fontWeight: '500',
    lineHeight: '14px',
  },
  headerIcon: {
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
  },
  headerContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '100%',
  },
  userButton: {
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('10px'),
    ...shorthands.padding('6px', '12px'),
    ...shorthands.borderRadius('4px'),
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  popoverSurface: {
    minWidth: '280px',
    ...shorthands.padding('16px'),
  },
  userInfoSection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    marginBottom: '12px',
  },
  userName: {
    fontSize: '16px',
    fontWeight: '600',
  },
  userEmail: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  logoutButton: {
    width: '100%',
    justifyContent: 'flex-start',
  },
  userIdentity: {
    color: 'white',
  },
  accountRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  popoverFooter: {
    marginTop: '12px',
  },
});

function AppHeader({ title, subtitle, icon, userAccount, onLogout, accentColor = tokens.colorBrandBackground }) {
  const classes = useStyles();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleLogout = () => {
    setIsPopoverOpen(false);
    onLogout();
  };

  return (
    <div className={classes.header} style={{ backgroundColor: accentColor }}>
      <div className={classes.headerContainer}>
        <div className={classes.headerTitle}>
          {icon && <span className={classes.headerIcon}>{icon}</span>}
          <div className={classes.titleStack}>
            <Text className={classes.titleText}>{title}</Text>
            {subtitle ? <Text className={classes.subtitleText}>{subtitle}</Text> : null}
          </div>
        </div>
        <div className={classes.headerRight}>
        {userAccount && (
          <Popover
            open={isPopoverOpen}
            onOpenChange={(e, data) => setIsPopoverOpen(data.open)}
            positioning="below-end"
            withArrow
          >
            <PopoverTrigger disableButtonEnhancement>
              <div className={classes.userButton}>
                <PersonRegular fontSize={20} />
                <Text className={classes.userIdentity}>
                  {userAccount.name || userAccount.username}
                </Text>
              </div>
            </PopoverTrigger>
            <PopoverSurface className={classes.popoverSurface}>
              <div className={classes.userInfoSection}>
                <div className={classes.accountRow}>
                  <Avatar
                    name={userAccount.name || userAccount.username}
                    size={48}
                    color="brand"
                  />
                  <div>
                    <Text className={classes.userName}>
                      {userAccount.name || userAccount.username}
                    </Text>
                    {userAccount.username && (
                      <div className={classes.userEmail}>
                        {userAccount.username}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <Divider />
              <div className={classes.popoverFooter}>
                <Button
                  appearance="subtle"
                  icon={<SignOut20Regular />}
                  onClick={handleLogout}
                  className={classes.logoutButton}
                >
                  Sign Out
                </Button>
              </div>
            </PopoverSurface>
          </Popover>
          )}
        </div>
      </div>
    </div>
  );
}

export default AppHeader;